import './i18n';
import React from 'react';
import ReactDOM from 'react-dom/client';

// Suppress the benign "ResizeObserver loop completed with undelivered
// notifications" error. It's a harmless layout-timing warning (notably thrown
// by @ant-design/charts on resize), but the webpack-dev-server error overlay
// treats any uncaught window error as a crash. The dev-server's own listener is
// registered first (its client is injected before our code), so a normal
// bubble-phase listener fires too late — we register in the CAPTURE phase so
// ours runs first and can stop that one message from reaching the overlay.
const __swallowResizeObserverErr = (e) => {
  const msg = e?.message || e?.reason?.message;
  if (typeof msg === 'string' && msg.includes('ResizeObserver loop')) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
};
window.addEventListener('error', __swallowResizeObserverErr, true);

// Belt-and-suspenders: defer ResizeObserver callbacks to the next animation
// frame. @ant-design/charts mutates layout inside its observer callback, which
// produces the "loop completed" warning; running the callback on the next frame
// breaks that synchronous resize→layout→resize loop so the error never fires.
const __RO = window.ResizeObserver;
if (__RO) {
  window.ResizeObserver = class extends __RO {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => callback(entries, observer));
      });
    }
  };
}
import './index.scss';
import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux';
import store from './redux';
import { RouterProvider } from 'react-router-dom';
import AppError from './App/AppError';
import AppMessage from './App/AppMessage';
import router from './App/router';
import { ConfigProvider, message, notification, theme as antdTheme } from 'antd';
import { initUiPrefs } from './utils/theme';
import useUiPrefs from './hooks/useUiPrefs';
import { tenantConfig } from './config/tenantConfig';
import { useTranslation } from 'react-i18next';
import enUS from 'antd/locale/en_US';
import frFR from 'antd/locale/fr_FR';
import 'react-phone-input-2/lib/material.css';

// The app header is `position: fixed; z-index: 9999` and the impersonation /
// dev banners sit at 10001-10002. AntD's static message/notification default
// to top:8, so every toast rendered behind the menu and was invisible
// ("popup I can't see"). Offset them below the header here; the stacking order
// (z-index: 10050, above all fixed chrome) is forced in index.scss because
// AntD 5.19's static config() accepts only `top` (no `zIndex`). (Static APIs
// don't read ConfigProvider in AntD v5.)
message.config({ top: 64, duration: 3 });
notification.config({ top: 64 });

// OVO-132 — a single-tenant instance re-brands the browser tab from env. (The
// in-app header logo/name come from tenantConfig too; OVO mode is untouched.)
if (tenantConfig.isSingleTenant) {
  document.title = tenantConfig.appName;
}

// Level-3 restyle — paint the stored theme/density before React mounts
// (no light→dark flash) and keep following the OS in 'system' mode.
initUiPrefs();

// Dark-mode overrides of the light tokens below. Mirrors the
// `:root[data-theme='dark']` block in styles/_theme.scss.
const DARK_TOKENS = {
  colorPrimary: '#ededef',
  colorTextLightSolid: '#121316',
  colorInfo: '#8da2ff',
  colorLink: '#8da2ff',
  colorLinkHover: '#a7b7ff',
  colorSuccess: '#5fd07a',
  colorWarning: '#ffb454',
  colorError: '#ff6b6b',
  colorTextBase: '#ededef',
  colorTextSecondary: '#b1b3b8',
  colorTextTertiary: '#7f8288',
  colorBgBase: '#121316',
  colorBgContainer: '#1a1b1e',
  colorBgElevated: '#222327',
  colorBgLayout: '#121316',
  colorBorder: '#34363c',
  colorBorderSecondary: '#2a2c31',
};
const DARK_COMPONENTS = {
  Table: {
    headerBg: '#1f2024',
    headerColor: '#b1b3b8',
    rowHoverBg: '#222327',
    rowSelectedBg: 'rgba(141, 162, 255, 0.16)',
    rowSelectedHoverBg: 'rgba(141, 162, 255, 0.22)',
  },
  Segmented: { trackBg: '#222327', itemSelectedBg: '#34363c' },
  Tabs: { inkBarColor: '#ededef', itemSelectedColor: '#ededef', itemHoverColor: '#ededef' },
  Input: {
    activeBorderColor: '#ededef',
    hoverBorderColor: '#7f8288',
    activeShadow: '0 0 0 3px rgba(237, 237, 239, 0.1)',
  },
  Select: { optionSelectedBg: 'rgba(141, 162, 255, 0.16)' },
  DatePicker: {
    cellHoverBg: 'rgba(141, 162, 255, 0.16)',
    activeBg: 'rgba(141, 162, 255, 0.16)',
    cellHoverWithRangeBg: 'rgba(141, 162, 255, 0.16)',
    cellActiveWithRangeBg: 'rgba(141, 162, 255, 0.16)',
  },
  Checkbox: { colorPrimary: '#ededef' },
  Switch: { colorPrimary: '#ededef', colorPrimaryHover: '#ffffff' },
};

const AntdLocaleWrapper = ({ children }) => {
  const { i18n } = useTranslation();
  const antdLocale = i18n.language === 'fr' ? frFR : enUS;
  const { resolved } = useUiPrefs();
  const isDark = resolved === 'dark';
  return (
    <ConfigProvider
      locale={antdLocale}
      theme={{
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        // Same design system as the driver app (ovo-driver-app/src/main.jsx):
        // black primary, ONE indigo accent for links/info/selection, so AntD's
        // default blue never leaks into focus rings, tags, links or spinners.
        // Values mirror src/styles/_variables.scss.
        token: {
          colorPrimary: '#212121',
          colorInfo: '#3b5bdb',
          colorLink: '#3b5bdb',
          colorLinkHover: '#2f4ac4',
          colorSuccess: '#2e7d32',
          colorWarning: '#f57c00',
          colorError: '#c62828',
          colorTextBase: '#212121',
          colorTextSecondary: '#616161',
          colorTextTertiary: '#9e9e9e',
          colorBorder: '#e0e0e0',
          colorBorderSecondary: '#eeeeee',
          colorBgLayout: '#f6f6f7',
          borderRadius: 8,
          borderRadiusSM: 6,
          borderRadiusLG: 12,
          // 36 / 32 / 44 — the old global `.ant-btn { height: 44px }` override
          // is gone; `size="small"` keeps its 32px (every page header relies
          // on it), default controls grow 32 → 36.
          controlHeight: 36,
          controlHeightSM: 32,
          controlHeightLG: 44,
          fontFamily: '"Figtree", sans-serif',
          fontSize: 14,
          ...(isDark ? DARK_TOKENS : {}),
        },
        components: {
          Button: { fontWeight: 600, paddingInline: 18, paddingInlineSM: 12 },
          Table: {
            headerBg: '#fafafa',
            headerColor: '#616161',
            headerSplitColor: 'transparent',
            cellPaddingBlock: 12,
            cellPaddingInline: 16,
            rowHoverBg: '#f6f6f7',
            rowSelectedBg: '#edf1fd',
            rowSelectedHoverBg: '#e4e9fa',
            headerBorderRadius: 8,
          },
          Card: { borderRadiusLG: 12 },
          Modal: { borderRadiusLG: 16 },
          Drawer: { paddingLG: 20 },
          Tag: { borderRadiusSM: 6 },
          Segmented: { trackBg: '#f0f0f0', itemSelectedBg: '#ffffff' },
          Tabs: { inkBarColor: '#212121', itemSelectedColor: '#212121', itemHoverColor: '#212121' },
          Input: {
            activeBorderColor: '#212121',
            hoverBorderColor: '#9e9e9e',
            activeShadow: '0 0 0 3px rgba(33, 33, 33, 0.08)',
          },
          Select: { optionSelectedBg: '#edf1fd' },
          DatePicker: {
            cellHoverBg: '#edf1fd',
            activeBg: '#edf1fd',
            cellHoverWithRangeBg: '#edf1fd',
            cellActiveWithRangeBg: '#edf1fd',
          },
          Checkbox: { colorPrimary: '#212121' },
          Switch: { colorPrimary: '#212121', colorPrimaryHover: '#000000' },
          ...(isDark ? DARK_COMPONENTS : {}),
        },
      }}
    >
      {children}
    </ConfigProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <AppError />
      <AppMessage />
      <AntdLocaleWrapper>
        <RouterProvider router={router} fallbackElement={<p>Initial Load...</p>} />
      </AntdLocaleWrapper>
    </Provider>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
